import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AdminService } from '../../services/AdminService/admin';
import { AuthService } from '../../services/AuthService/auth';
import { UserResponse } from '../../models/company.dto';
import { Role } from '../../models/auth.dto';
import { extractErrorMessage } from '../../shared/error-utils';
import { StatusBadge } from '../../shared/components/status-badge/status-badge';
import { ErrorBanner } from '../../shared/components/error-banner/error-banner';
import { EmptyState } from '../../shared/components/empty-state/empty-state';
import { FilterSelect, FilterOption } from '../../shared/components/filter-select/filter-select';
import { PageSizeSelect } from '../../shared/components/page-size-select/page-size-select';
import { CreateUserDialog } from '../../dialogs/create-user-dialog/create-user-dialog';
import { ChangeRoleDialog } from '../../dialogs/change-role-dialog/change-role-dialog';
import { DisableUserDialog } from '../../dialogs/disable-user-dialog/disable-user-dialog';
import { ConfirmDialog } from '../../dialogs/confirm-dialog/confirm-dialog';

const ROLES: Role[] = ['SUPERADMIN', 'ADMIN', 'COMPANY_ADMIN', 'COMPANY_USER', 'CUSTOMS', 'AUDITOR'];

@Component({
  selector: 'app-platform-users',
  imports: [CommonModule, FormsModule, MatButtonModule, MatIconModule, MatTooltipModule, StatusBadge, ErrorBanner, EmptyState, FilterSelect, PageSizeSelect],
  templateUrl: './platform-users.html',
  styleUrl: './platform-users.scss',
})
export class PlatformUsers implements OnInit {
  roleOptions: FilterOption[] = ROLES.map((r) => ({ value: r, label: r }));
  roleFilter: Role | '' = '';

  users = signal<UserResponse[]>([]);
  isLoading = signal(false);
  errorMessage = signal('');

  togglingUserId = signal<string | null>(null);

  readonly pageSizeOptions = [10, 20, 50, 100];
  pageSize = signal(10);
  page = signal(0);
  totalPages = signal(1);
  totalCount = signal(0);

  constructor(private admin: AdminService, private auth: AuthService, private dialog: MatDialog) {}

  get ownUserId(): string | undefined {
    return this.auth.getSession()?.userId;
  }

  get rangeStart(): number {
    return this.totalCount() === 0 ? 0 : this.page() * this.pageSize() + 1;
  }

  get rangeEnd(): number {
    return Math.min((this.page() + 1) * this.pageSize(), this.totalCount());
  }

  get roleFilterLabel(): string {
    if (!this.roleFilter) {
      return 'All roles';
    }
    return this.roleFilter.charAt(0) + this.roleFilter.slice(1).toLowerCase().replace(/_/g, ' ');
  }

  ngOnInit(): void {
    this.load();
  }

  onFilterChange(): void {
    this.page.set(0);
    this.load();
  }

  onPageSizeChange(size: number): void {
    this.pageSize.set(size);
    this.page.set(0);
    this.load();
  }

  load(): void {
    this.isLoading.set(true);
    this.errorMessage.set('');

    this.admin.listUsers(this.roleFilter || undefined, this.page(), this.pageSize()).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        this.users.set(res.data);
        this.totalPages.set(res.page?.total_pages ?? 1);
        this.totalCount.set(res.page?.count ?? res.data.length);
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading.set(false);
        this.errorMessage.set(extractErrorMessage(err, 'Failed to load users.'));
      },
    });
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages() || page === this.page()) {
      return;
    }
    this.page.set(page);
    this.load();
  }

  /** Page numbers to render, with -1 as an ellipsis placeholder, e.g. [0, -1, 4, 5, 6, -1, 19]. */
  get pageWindow(): number[] {
    const total = this.totalPages();
    const current = this.page();
    const pages: number[] = [];
    const add = (p: number) => {
      if (pages[pages.length - 1] !== p) {
        pages.push(p);
      }
    };

    add(0);
    for (let p = current - 1; p <= current + 1; p++) {
      if (p > 0 && p < total - 1) {
        if (p > pages[pages.length - 1] + 1) {
          add(-1);
        }
        add(p);
      }
    }
    if (total - 1 > pages[pages.length - 1] + 1) {
      add(-1);
    }
    if (total > 1) {
      add(total - 1);
    }
    return pages;
  }

  openCreateDialog(): void {
    this.dialog
      .open(CreateUserDialog, { width: '480px' })
      .afterClosed()
      .subscribe((created) => {
        if (created) {
          this.load();
        }
      });
  }

  openChangeRoleDialog(user: UserResponse): void {
    this.dialog
      .open(ChangeRoleDialog, {
        width: '480px',
        maxHeight: '90vh',
        data: { userId: user.id, userEmail: user.email, currentRole: user.role },
      })
      .afterClosed()
      .subscribe((changed) => {
        if (changed) {
          this.load();
        }
      });
  }

  toggleUser(user: UserResponse): void {
    if (this.isCompanyLocked(user)) {
      return;
    }

    if (user.enabled) {
      this.openDisableDialog(user);
      return;
    }

    this.dialog
      .open(ConfirmDialog, {
        width: '420px',
        data: {
          title: 'Enable this user?',
          message: `"${user.email}" will regain access and be able to log in again.`,
          confirmLabel: 'Enable',
          cancelLabel: 'Cancel',
          danger: false,
          icon: 'check_circle',
        },
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.applyEnable(user);
        }
      });
  }

  private applyEnable(user: UserResponse): void {
    this.togglingUserId.set(user.id);
    this.admin.enableUser(user.id).subscribe({
      next: () => {
        this.togglingUserId.set(null);
        this.load();
      },
      error: (err: HttpErrorResponse) => {
        this.togglingUserId.set(null);
        this.errorMessage.set(extractErrorMessage(err, 'Failed to update user.'));
      },
    });
  }

  isCompanyLocked(user: UserResponse): boolean {
    return user.companyStatus === 'PENDING' || user.companyStatus === 'REJECTED';
  }

  toggleTooltip(user: UserResponse): string {
    if (this.isCompanyLocked(user)) {
      return `Company registration is ${user.companyStatus} — this account can't log in regardless of this setting`;
    }
    return '';
  }

  private openDisableDialog(user: UserResponse): void {
    this.dialog
      .open(DisableUserDialog, {
        width: '480px',
        data: { userId: user.id, userEmail: user.email },
      })
      .afterClosed()
      .subscribe((disabled) => {
        if (disabled) {
          this.load();
        }
      });
  }
}
